import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePackages } from "@/api/packages.js";
import { formatCents, packageFaq } from "@/utils/packageFeatures.js";
import { landingFacts, shortNames } from "@/utils/landingCatalog.js";
import {
  ArtworkDefs,
  FeatureIcon,
  HeroArt,
  PanelArt,
  Person,
  StepArt,
} from "./Artwork.jsx";
import Calculator from "./Calculator.jsx";
import PricingPlans from "./PricingPlans.jsx";
import { prefersCalm, scrollToSection } from "./motion.js";
import { PRERENDERED_CATALOG_ID } from "@/utils/seo.js";

const HERO_WORDS = [
  "contadores",
  "despachos",
  "contadoras independientes",
  "tus clientes pyme",
];

/** «Para contadores / despachos / …»: cambia cada 2.6 s con un desvanecido. */
const RotatingWord = () => {
  const [index, setIndex] = useState(0);
  const [out, setOut] = useState(false);

  useEffect(() => {
    if (prefersCalm()) return undefined;
    let swap;
    const timer = setInterval(() => {
      setOut(true);
      swap = setTimeout(() => {
        setIndex((i) => (i + 1) % HERO_WORDS.length);
        setOut(false);
      }, 300);
    }, 2600);
    return () => {
      clearInterval(timer);
      clearTimeout(swap);
    };
  }, []);

  return (
    <span className={`lp-rot${out ? " lp-out" : ""}`}>{HERO_WORDS[index]}</span>
  );
};

/** Cifra que cuenta desde cero la primera vez que entra a la pantalla. */
const CountUp = ({ to, decimals = 0 }) => {
  const ref = useRef(null);
  const [value, setValue] = useState(to);

  useEffect(() => {
    if (prefersCalm()) return undefined;
    let frame;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        let start = null;
        const step = (now) => {
          start ??= now;
          const k = Math.min(1, (now - start) / 900);
          setValue(to * (1 - Math.pow(1 - k, 3)));
          if (k < 1) frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
      },
      { threshold: 0.6 },
    );
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [to]);

  return <span ref={ref}>{value.toFixed(decimals)}</span>;
};

/**
 * Teléfono o correo con botón «Copiar». No se depende de tel: ni mailto:, que
 * en escritorio muchas veces no abren nada.
 */
const CopyRow = ({ value }) => {
  const ref = useRef(null);
  const [copied, setCopied] = useState(false);

  const selectText = () => {
    const range = document.createRange();
    range.selectNodeContents(ref.current);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const copy = () => {
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    };
    try {
      navigator.clipboard.writeText(value).then(done, selectText);
    } catch {
      selectText();
    }
  };

  return (
    <div className="lp-row">
      <span ref={ref}>{value}</span>
      <button type="button" className="lp-copy" onClick={copy}>
        {copied ? "Copiado" : "Copiar"}
      </button>
    </div>
  );
};

/**
 * Aparición al hacer scroll: lo que está debajo de la pantalla baja unos px y
 * sube al entrar. Nada queda invisible si esto falla.
 */
const useReveal = (ref, ready) => {
  useEffect(() => {
    if (!ready || prefersCalm() || !("IntersectionObserver" in window))
      return undefined;
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.remove("lp-pre");
          observer.unobserve(entry.target);
        }),
      { threshold: 0.12 },
    );
    ref.current.querySelectorAll(".lp-reveal").forEach((el, i) => {
      if (el.getBoundingClientRect().top <= window.innerHeight) return;
      el.classList.add("lp-pre");
      el.style.transitionDelay = `${(i % 4) * 70}ms`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, [ref, ready]);
};

const Avatar = ({ look, color, delay, style, children }) => (
  <div
    className={`lp-av lp-bob${delay ? ` d${delay}` : ""}`}
    style={{ "--c": `var(--${color})`, ...style }}
  >
    <Person look={look} />
    {children}
  </div>
);

/** Las personas del demo, para no repetir sus rasgos en cada sección. */
const LOOKS = {
  contadora: {
    skin: "#F3C7A5",
    hair: "#2B2140",
    shirt: "#4977CC",
    long: 1,
    gl: 1,
  },
  contador: { skin: "#C98F66", hair: "#1B1B1B", shirt: "#1B466B", gl: 1 },
  duena: { skin: "#8D5A3C", hair: "#3A2418", shirt: "#E98A6B", long: 1 },
  emprendedor: { skin: "#F0C09A", hair: "#D9A441", shirt: "#7AB3CC" },
  hero2: { skin: "#B97A52", hair: "#1B1B1B", shirt: "#FFC85A" },
  hoy: { skin: "#F3C7A5", hair: "#2B2140", shirt: "#8A97A3", long: 1 },
  conCfdi: {
    skin: "#F3C7A5",
    hair: "#2B2140",
    shirt: "#1F9D6B",
    long: 1,
    gl: 1,
  },
  paso2: { skin: "#C98F66", hair: "#1B1B1B", shirt: "#1B466B" },
  faq: { skin: "#F0C09A", hair: "#D9A441", shirt: "#4977CC", gl: 1 },
  agente1: {
    skin: "#E8B48C",
    hair: "#5A3A22",
    shirt: "#4977CC",
    long: 1,
    hs: 1,
  },
  agente2: { skin: "#B97A52", hair: "#1B1B1B", shirt: "#F9B79A", hs: 1 },
};

/** Ejemplo del panel: datos ficticios, y la tabla lo dice. */
const SAMPLE_ISSUERS = [
  ["Abarrotes La Esquina", "AAE010101AB1", ["Vigente", "Vigente", "Cargada"]],
  [
    "Taller Mecánico Ruiz",
    "TMR020202CD2",
    ["Vigente", "Por renovar", "Cargada"],
  ],
  [
    "Consultoría Norte",
    "CNO030303EF3",
    ["Por subir", "Por subir", "Pendiente"],
  ],
];
const OK_STATES = ["Vigente", "Cargada"];

/**
 * El catálogo con el que se pre-generó la portada (scripts/prerender.mjs lo
 * deja en la página). Arrancar con ese mismo catálogo es lo que permite que
 * React retome el HTML en vez de volver a pintarlo.
 */
const prerenderedCatalog = () => {
  if (typeof document === "undefined") return undefined;
  const script = document.getElementById(PRERENDERED_CATALOG_ID);
  try {
    return script ? JSON.parse(script.textContent) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Portada pública para contadores (tarjeta #13), según el demo aprobado. Lo
 * que dice de los paquetes —precios, vigencias, quién trae asesorías— sale del
 * catálogo; lo demás es el texto del demo.
 */
const Landing = ({ initialPackages = prerenderedCatalog() }) => {
  const { t, i18n } = useTranslation();
  const { packages, loading, error } = usePackages(initialPackages);
  const { hash } = useLocation();
  const pageRef = useRef(null);
  const facts = landingFacts(packages);
  const {
    catalog,
    limited,
    firstUnlimited,
    firstWithAdvisories,
    withHistory,
    allIncluded,
    bestPerFolio,
    topSavings,
    topSavingsPercent,
  } = facts;
  const ready = !loading;

  useReveal(pageRef, ready);

  // Desde el login o el alta, «Paquetes» llega como /#precios: se espera al
  // catálogo, que cambia la altura de la página, y se coloca ahí sin animar.
  useEffect(() => {
    if (ready && hash) scrollToSection(hash.slice(1), { instant: true });
  }, [ready, hash]);

  const faq = packageFaq(catalog, t, i18n.language);

  return (
    <main className="lp-page" ref={pageRef}>
      <ArtworkDefs />

      <header className="lp-hero" id="inicio">
        <div className="lp-confetti" aria-hidden="true">
          <span className="lp-c1" />
          <span className="lp-c2" />
          <span className="lp-c3" />
          <span className="lp-c4" />
        </div>
        <div style={{ position: "relative", zIndex: 1 }}>
          <p className="lp-eyebrow">
            Para <RotatingWord />
          </p>
          <h1 style={{ marginTop: 12 }}>
            Factura para tus clientes{" "}
            <span className="lp-accent">sin pagar por mes</span> lo que no usas.
          </h1>
          <p className="lp-lead">
            Compras folios y los usas cuando los necesitas
            {firstUnlimited && (
              <>; desde el {firstUnlimited.name}, no caducan</>
            )}
            . Un panel para ver las constancias y las opiniones de cumplimiento
            de cada emisor
            {firstWithAdvisories && (
              <>, y asesoría fiscal incluida en los paquetes grandes</>
            )}
            .
          </p>
          <div className="lp-ctas">
            <button
              type="button"
              className="lp-btn"
              onClick={() => scrollToSection("contacto")}
            >
              Quiero contratar
            </button>
            <button
              type="button"
              className="lp-btn lp-ghost"
              onClick={() => scrollToSection("calculadora")}
            >
              Calcular mi paquete
            </button>
          </div>
          <div className="lp-proof">
            <span>Sin plazos forzosos</span>
            {firstUnlimited && (
              <span>
                Folios sin vencimiento desde{" "}
                {formatCents(firstUnlimited.priceCents)}
              </span>
            )}
            <span>PDF y XML siempre a la mano</span>
          </div>
        </div>

        <div className="lp-illo">
          <HeroArt />
          <Avatar
            look={LOOKS.contadora}
            color="peach"
            style={{ left: "2%", bottom: 0 }}
          />
          <Avatar
            look={LOOKS.hero2}
            color="teal"
            delay={2}
            style={{ right: "2%", bottom: 0 }}
          />
          <div
            className="lp-bubble"
            style={{ left: 0, bottom: "30%", transform: "translateY(-100%)" }}
          >
            Tu factura ya salió
          </div>
        </div>
      </header>

      {catalog.length > 0 && (
        <div className="lp-stats">
          <div className="lp-stat lp-s1 lp-reveal">
            <b>
              <CountUp to={catalog.length} />
            </b>
            <span>paquetes para elegir</span>
          </div>
          {bestPerFolio && (
            <div className="lp-stat lp-s2 lp-reveal">
              <b>
                $
                <CountUp
                  to={bestPerFolio.pricePerFolioCents / 100}
                  decimals={bestPerFolio.pricePerFolioCents % 100 ? 2 : 0}
                />
              </b>
              <span>por folio, desde el {bestPerFolio.name}</span>
            </div>
          )}
          {topSavingsPercent > 0 && (
            <div className="lp-stat lp-s3 lp-reveal">
              <b>
                <CountUp to={topSavingsPercent} />%
              </b>
              <span>de ahorro en el {topSavings.name}</span>
            </div>
          )}
          {firstUnlimited && (
            <div className="lp-stat lp-s4 lp-reveal">
              <b>∞</b>
              <span>vigencia de folios desde el {firstUnlimited.name}</span>
            </div>
          )}
        </div>
      )}

      <div className="lp-band">
        <div className="lp-wrap">
          <section id="quien" style={{ paddingTop: 24 }}>
            <div className="lp-center">
              <p className="lp-eyebrow">Para quién es</p>
              <h2>Hay un paquete para cada forma de trabajar.</h2>
              <p>
                Según cuántas facturas emitas, el paquete que conviene cambia.
                Elige el que se parece a ti.
              </p>
            </div>
            <div className="lp-who">
              <div className="lp-card lp-reveal">
                <Avatar look={LOOKS.contadora} color="peach-soft" />
                <h3>Contadora independiente</h3>
                <p>Pocas facturas al año. Compras lo justo y listo.</p>
                {limited.length > 0 && (
                  <span className="lp-tagp">
                    Paquete {shortNames(limited, "disjunction")}
                  </span>
                )}
              </div>
              <div className="lp-card lp-reveal">
                <Avatar look={LOOKS.contador} color="yellow-soft" delay={1} />
                <h3>Despacho que factura seguido</h3>
                <p>Facturas de forma constante y tus folios nunca caducan.</p>
                {firstUnlimited && (
                  <span className="lp-tagp">
                    {firstUnlimited.name} en adelante
                  </span>
                )}
              </div>
              <div className="lp-card lp-reveal">
                <Avatar look={LOOKS.duena} color="teal-soft" delay={2} />
                <h3>Despacho con respaldo fiscal</h3>
                <p>
                  Atención prioritaria, historial de operaciones y asesorías.
                </p>
                {firstWithAdvisories && (
                  <span className="lp-tagp">{firstWithAdvisories.name}</span>
                )}
              </div>
              <div className="lp-card lp-reveal">
                <Avatar look={LOOKS.emprendedor} color="blue-soft" delay={3} />
                <h3>Operación grande</h3>
                <p>
                  Todo incluido
                  {allIncluded && (
                    <>
                      , con {allIncluded.advisories} asesorías y{" "}
                      {allIncluded.consultations} consultas fiscales o contables
                    </>
                  )}
                  .
                </p>
                {allIncluded && (
                  <span className="lp-tagp">{allIncluded.name}</span>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      <div className="lp-band lp-peach">
        <div className="lp-wrap">
          <section id="antes">
            <div className="lp-sechead">
              <h2>Facturar para otros no debería ser otro despacho.</h2>
              <p>
                Cada factura de cliente que se atora es una llamada, un
                reproceso y una hora que no cobras. CFD-iPunto quita el trámite
                y deja el control en tu mano.
              </p>
            </div>
            <div className="lp-vs">
              <div className="lp-card lp-reveal">
                <div className="lp-top">
                  <h3>Hoy</h3>
                  <Avatar look={LOOKS.hoy} color="peach">
                    <svg
                      className="lp-mood"
                      viewBox="0 0 34 34"
                      aria-hidden="true"
                    >
                      <path
                        d="M17 3c6 8 9 12 9 17a9 9 0 0 1-18 0c0-5 3-9 9-17z"
                        fill="var(--teal)"
                      />
                    </svg>
                  </Avatar>
                </div>
                <ul>
                  <li>Mensualidades por facturar aunque ese mes no emitas.</li>
                  <li>Folios que vencen y se pierden.</li>
                  <li>
                    Constancias y opiniones de cumplimiento regadas en correos.
                  </li>
                  <li>Preguntas fiscales sin a quién hacerlas.</li>
                </ul>
              </div>
              <div className="lp-card lp-dark lp-reveal">
                <div className="lp-top">
                  <h3>Con CFD-iPunto</h3>
                  <Avatar look={LOOKS.conCfdi} color="yellow" delay={2}>
                    <svg
                      className="lp-mood"
                      viewBox="0 0 34 34"
                      aria-hidden="true"
                    >
                      <path
                        d="M17 3l3.5 9 9.5 .8-7.3 6.2 2.3 9.3L17 23l-8 5.3 2.3-9.3L4 12.8 13.5 12z"
                        fill="#fff"
                      />
                    </svg>
                  </Avatar>
                </div>
                <ul>
                  <li>
                    Pagas por paquete de folios. Si no facturas, no pagas.
                  </li>
                  {firstUnlimited && (
                    <li>
                      Del {firstUnlimited.name} en adelante, la vigencia es
                      ilimitada.
                    </li>
                  )}
                  <li>
                    Constancia de Situación Fiscal y 32/D por emisor, en un solo
                    lugar.
                  </li>
                  {firstWithAdvisories && (
                    <li>
                      Asesorías fiscales incluidas desde el{" "}
                      {firstWithAdvisories.name}.
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div className="lp-band lp-blue">
        <div className="lp-wrap">
          <section id="panel">
            <div className="lp-sechead">
              <h2>Tu panel de contador, de un vistazo.</h2>
              <p>
                Lista de emisores con su constancia y su opinión de cumplimiento
                32/D; desde cada fila subes o consultas el documento. Los datos
                de abajo son un ejemplo.
              </p>
            </div>
            <div className="lp-panelgrid">
              <div className="lp-illo">
                <PanelArt />
              </div>
              <div className="lp-panel lp-reveal">
                <div className="lp-bar">
                  <span
                    className="lp-dot"
                    style={{ background: "var(--coral)" }}
                  />
                  <span
                    className="lp-dot"
                    style={{ background: "var(--yellow)" }}
                  />
                  <span
                    className="lp-dot"
                    style={{ background: "var(--ok)" }}
                  />
                  <b>Lista de emisores</b>
                  <span className="lp-eyebrow">Ejemplo</span>
                </div>
                <div className="lp-tablewrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Emisor</th>
                        <th>RFC</th>
                        <th>Constancia</th>
                        <th>32/D</th>
                        <th>Firma</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SAMPLE_ISSUERS.map(([name, rfc, states]) => (
                        <tr key={rfc}>
                          <td>{name}</td>
                          <td className="lp-num">{rfc}</td>
                          {states.map((state, i) => (
                            <td key={i}>
                              <span
                                className={`lp-pill ${OK_STATES.includes(state) ? "lp-ok" : "lp-warn"}`}
                              >
                                {state}
                              </span>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="lp-feats">
              <div className="lp-feat lp-reveal">
                <div className="lp-ico">
                  <FeatureIcon index={0} />
                </div>
                <h3>Constancias a la mano</h3>
                <p>
                  Consulta y descarga la Constancia de Situación Fiscal de cada
                  emisor sin pedirla otra vez.
                </p>
              </div>
              <div className="lp-feat lp-reveal">
                <div className="lp-ico">
                  <FeatureIcon index={1} />
                </div>
                <h3>Cumplimiento 32/D</h3>
                <p>
                  Sube y consulta la opinión de cumplimiento, con su versión y
                  su vigencia.
                </p>
              </div>
              <div className="lp-feat lp-reveal">
                <div className="lp-ico">
                  <FeatureIcon index={2} />
                </div>
                <h3>Historial de operaciones</h3>
                <p>
                  {withHistory.length > 0
                    ? `En los paquetes ${shortNames(withHistory)} ves qué se emitió y cuándo.`
                    : "Ves qué se emitió y cuándo."}
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div className="lp-band lp-yellow">
        <div className="lp-wrap">
          <section id="como">
            <div className="lp-center">
              <p className="lp-eyebrow">Cómo funciona</p>
              <h2>Emitir toma tres pasos.</h2>
              <p>
                Sin instalar nada. Entras a la app, capturas y esperas el aviso
                en tu celular.
              </p>
            </div>
            <div className="lp-steps">
              {[
                [
                  LOOKS.contadora,
                  "blue-soft",
                  "Entra a la app",
                  "Ingresa a CFD-iPunto con tu cuenta.",
                ],
                [
                  LOOKS.paso2,
                  "teal-soft",
                  "Captura los datos",
                  "Ingresa los datos de la factura que vas a emitir.",
                ],
                [
                  LOOKS.duena,
                  "peach-soft",
                  "Recibe el aviso",
                  "Te llega una notificación en el celular cuando ya está lista, con su PDF y su XML.",
                ],
              ].map(([look, color, title, text], step) => (
                <div className="lp-step lp-reveal" key={title}>
                  <Avatar look={look} color={color} delay={step} />
                  <StepArt step={step} />
                  <span className="lp-tag">PASO {step + 1}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <div className="lp-band lp-teal">
        <div className="lp-wrap">
          <Calculator packages={packages} />
        </div>
      </div>

      <PricingPlans packages={packages} loading={loading} error={error} />

      {faq.length > 0 && (
        <div className="lp-band lp-blue">
          <div className="lp-wrap">
            <section id="faq">
              <div className="lp-center lp-faqhead">
                <Avatar look={LOOKS.faq} color="yellow" />
                <div className="lp-bubble lp-y">¿Una duda?</div>
                <p className="lp-eyebrow">Preguntas frecuentes</p>
                <h2>{t("packages.faqTitle")}</h2>
                <p>{t("packages.faqSubtitle")}</p>
              </div>
              <div style={{ maxWidth: 760, marginInline: "auto" }}>
                {faq.map((item, i) => (
                  <details key={item.key} open={i === 0}>
                    <summary>{item.question}</summary>
                    <p>{item.answer}</p>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </div>
      )}

      <section id="contacto" style={{ paddingBlock: "72px 40px" }}>
        <div className="lp-cta">
          <span className="lp-blob1" />
          <span className="lp-blob2" />
          <div style={{ position: "relative" }}>
            <p className="lp-eyebrow">Empieza esta semana</p>
            <h2 style={{ marginTop: 8 }}>
              Cuéntanos cuántos clientes facturas y te armamos el paquete.
            </h2>
            <p>Sin plazos forzosos y con línea exclusiva y personalizada.</p>
            <p style={{ marginTop: 22 }}>
              <Link className="lp-btn lp-yellow" to="/dashboard">
                Entrar a la aplicación
              </Link>
            </p>
          </div>
          <div className="lp-agents">
            <div className="lp-bubble lp-y" style={{ left: "6%" }}>
              ¿Te armamos tu paquete?
            </div>
            <Avatar look={LOOKS.agente1} color="yellow" />
            <Avatar look={LOOKS.agente2} color="teal" delay={2} />
          </div>
          <div className="lp-contact">
            <div className="lp-contactgrid">
              <CopyRow value={t("phone company")} />
              <CopyRow value={t("company email")} />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Landing;
